import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@features/auth/context/AuthContext';
import {
  isInAppBrowser,
  isIOSDevice,
  getInAppBrowserName,
  openInExternalBrowser,
} from '@/utils/browserDetect';

export default function MainMenuPage() {
  const {
    user,
    profile,
    isLoading: authLoading,
    isAdmin,
    signOut,
    signInWithGoogle,
  } = useAuth();
  const [showInAppBrowserWarning, setShowInAppBrowserWarning] = useState(false);

  useEffect(() => {
    if (!user && isInAppBrowser() && isIOSDevice()) {
      setShowInAppBrowserWarning(true);
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">로딩 중...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-primary-red to-primary-blue flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">찬송가 아카이브</h1>
          <p className="text-gray-500 mb-8">한국찬송가공회 찬송가 데이터베이스</p>

          {showInAppBrowserWarning && (
            <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-left flex-1">
                  <h3 className="font-semibold text-amber-900 mb-1">
                    {getInAppBrowserName()}에서는 로그인할 수 없습니다
                  </h3>
                  <p className="text-sm text-amber-800 mb-3">
                    구글 로그인은 보안상의 이유로 앱 내장 브라우저를 지원하지 않습니다.
                  </p>
                </div>
              </div>
              <button
                onClick={() => openInExternalBrowser()}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-2.5 px-4 rounded-lg transition-colors font-medium"
              >
                Chrome/Safari에서 열기
              </button>
              <p className="text-xs text-amber-700 mt-3">
                또는 이 페이지 URL을 복사해서 Chrome이나 Safari에서 직접 열어주세요.
              </p>
            </div>
          )}

          <button
            onClick={signInWithGoogle}
            disabled={showInAppBrowserWarning}
            className={`w-full border-2 py-3 px-6 rounded-lg transition-colors font-medium text-lg flex items-center justify-center gap-3 ${
              showInAppBrowserWarning
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google로 로그인
          </button>

          <p className="text-xs text-gray-500 text-center mt-6">
            로그인하시면{' '}
            <a href="#" className="text-primary-blue hover:underline">이용약관</a>과{' '}
            <a href="#" className="text-primary-blue hover:underline">개인정보처리방침</a>에
            동의하는 것으로 간주됩니다.
          </p>
        </div>
      </main>
    );
  }

  const displayName = profile?.display_name || profile?.email || '사용자';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 로고 */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-center">
            <picture>
              <source srcSet="/hymn_logo.webp" type="image/webp" />
              <img
                src="/hymn_logo.png"
                alt="재단법인 한국찬송가공회"
                className="h-10 object-contain"
                loading="eager"
              />
            </picture>
          </div>
        </div>
      </div>

      {/* 헤더 */}
      <header className="bg-primary-red text-white py-3 px-4">
        <div className="container mx-auto flex items-center justify-end gap-3">
          <span className="text-xs md:text-sm bg-primary-red/70 px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
            {displayName}
          </span>
          {isAdmin && (
            <Link
              to="/admin"
              className="text-xs md:text-sm bg-primary-gold hover:bg-secondary-brown px-2 md:px-3 py-1 rounded-lg transition-colors whitespace-nowrap"
            >
              관리자 페이지
            </Link>
          )}
          <button
            onClick={() => signOut()}
            className="text-xs md:text-sm bg-white/20 hover:bg-white/30 px-2 md:px-3 py-1 rounded-lg transition-colors whitespace-nowrap"
          >
            로그아웃
          </button>
        </div>
      </header>

      {/* 메인 메뉴 */}
      <main className="container mx-auto px-4 py-10 md:py-16">
        <h2 className="text-center text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          무엇을 찾으세요?
        </h2>
        <p className="text-center text-gray-500 mb-10 md:mb-14">찬송가 또는 성경을 선택하세요</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* 찬송가 카드 */}
          <Link
            to="/hymns"
            className="group flex flex-col items-center justify-center gap-4 bg-white rounded-2xl shadow-md hover:shadow-xl border-2 border-transparent hover:border-primary-red transition-all duration-200 p-8 md:p-10"
          >
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-red/10 rounded-full flex items-center justify-center group-hover:bg-primary-red/20 transition-colors">
              <svg className="w-8 h-8 md:w-10 md:h-10 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-xl md:text-2xl font-bold text-gray-800 mb-1">찬송가</p>
              <p className="text-sm text-gray-500">한국찬송가공회 645곡</p>
            </div>
            <span className="text-primary-red text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              찬송가 보기
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>

          {/* 성경 카드 */}
          <Link
            to="/bible"
            className="group flex flex-col items-center justify-center gap-4 bg-white rounded-2xl shadow-md hover:shadow-xl border-2 border-transparent hover:border-primary-blue transition-all duration-200 p-8 md:p-10"
          >
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-blue/10 rounded-full flex items-center justify-center group-hover:bg-primary-blue/20 transition-colors">
              <svg className="w-8 h-8 md:w-10 md:h-10 text-primary-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-xl md:text-2xl font-bold text-gray-800 mb-1">성경</p>
              <p className="text-sm text-gray-500">개역개정 성경</p>
            </div>
            <span className="text-primary-blue text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              성경 읽기
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>
        </div>
      </main>
    </div>
  );
}
